import React from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import CustomButton from "../customButtom/CustomButton";
import CommonDropdown from "../commonDropDown/CommonDropDown";

const SelectionView = ({
  state,
  setPartial,
  targetOcc,
  targetOccupationFromRedux,
  targetJob,
  currentJob,
  skillsCategories,
  selectedSkills,
  toggleSkill,
  handleAnalyzeGap,
  fetchTargetOccupationSkills,
  register,
  errors
}) => {
  return (
    <>
      <div className="p-3 sm:p-4 border-b">
        <div className={`${(state.isLoadingTargetCareers || state.isInitialCareerFetch) ? "opacity-60 pointer-events-none" : ""}`}>
          <CommonDropdown
            key={`target-occupation-${state.currentView}-${state.lastBackNavigation || 0}`}
            label="Target Occupation"
            name="targetOccupation"
            required
            register={register}
            errors={errors}
            options={state.targetOccupationOptions}
            search
            defaultValue={targetOcc || targetOccupationFromRedux || targetJob || state.targetOccupation || ""}
            labelClass="text-sm font-medium text-gray-700"
            readOnly={state.isLoadingTargetCareers || state.isInitialCareerFetch}
          />
          {(state.isLoadingTargetCareers || state.isInitialCareerFetch) && (
            <div className="flex items-center justify-center gap-2 mt-3 py-4 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin text-[#6e28cc]" />
              <span>Loading target careers...</span>
            </div>
          )}
        </div>
      </div>

      {!(state.isLoadingTargetCareers || state.isInitialCareerFetch) && (
        <>
          {!targetOcc && !targetOccupationFromRedux && !targetJob ? (
            <div className="p-3 sm:p-4 border-b">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <ArrowRight className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Select Target Career</h3>
                <p className="text-sm text-gray-500 max-w-md">
                  Choose a target occupation from the dropdown above to see the skills required and start your gap analysis.
                </p>
              </div>
            </div>
          ) : targetOcc && !state.targetOccupationOptions.find(c => c.name === targetOcc) ? (
            <div className="p-3 sm:p-4 border-b">
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                  <ArrowRight className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Select Valid Target Career</h3>
                <p className="text-sm text-gray-500 max-w-md">
                  Please select a target occupation from the dropdown options above based on your current occupation to proceed with the skills gap analysis.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-3 sm:p-4 border-b">
              <h3 className="text-sm sm:text-base font-semibold mb-1">Skills & Competencies</h3>
              <p className="text-xs sm:text-sm text-gray-600 mb-3">Select skills you possess for the target occupation</p>
              {state.isLoadingSkills ? (
                <div className="flex flex-col items-center justify-center py-12 text-sm text-gray-600">
                  <Loader2 className="w-8 h-8 animate-spin text-[#6e28cc] mb-3" />
                  <span>Loading skills for target occupation...</span>
                </div>
              ) : (
                <>
                  {state.skillsError && !state.skillsLoaded && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                      {state.skillsError}
                      <button 
                        onClick={() => {
                          const career = state.targetOccupationOptions.find(c => c.name === targetOcc);
                          if (career) {
                            setPartial({ skillsError: null, isLoadingSkills: true });
                            fetchTargetOccupationSkills(targetOcc, career.type);
                          }
                        }} 
                        className="block mt-2 text-xs text-[#6e28cc] hover:underline"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                  <div className="space-y-4">
                    {Object.entries(skillsCategories).map(([k, { title, description, skills }]) => (
                      <div key={k} className="border rounded-lg p-3">
                        <h4 className="font-medium text-sm mb-1">{title}</h4>
                        <p className="text-xs text-gray-600 mb-2">{description}</p>
                        <div className="flex flex-wrap gap-2">
                          {skills.length ? skills.map(skill => (
                            <button 
                              key={skill} 
                              onClick={() => toggleSkill(skill)}
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                selectedSkills.has(skill) 
                                  ? "bg-[#6e28cc] text-white" 
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {skill}
                            </button>
                          )) : (
                            <span className="text-xs italic text-gray-500">No skills available</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-gray-600">Selected: {selectedSkills.size} skills</div>
                </>
              )}
            </div>
          )}
        </>
      )}

      <div className="p-3 sm:p-4 bg-gray-50 rounded-b-xl">
        <CustomButton
          label={state.isLoadingSkills ? "Loading Skills..." : "Analyze Skills Gap"}
          onClick={handleAnalyzeGap}
          disabled={
            !currentJob || 
            !targetOcc || 
            targetOcc === "" || 
            !state.targetOccupationOptions.find(c => c.name === targetOcc) || 
            state.isLoadingTargetCareers || 
            state.isInitialCareerFetch || 
            state.isLoadingSkills
          }
          buttonClass={`w-full py-2 rounded-lg text-sm sm:text-base font-medium ${
            !currentJob || 
            !targetOcc || 
            targetOcc === "" || 
            !state.targetOccupationOptions.find(c => c.name === targetOcc) || 
            state.isLoadingTargetCareers || 
            state.isInitialCareerFetch || 
            state.isLoadingSkills
              ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
              : "bg-[#6e28cc] hover:bg-[#5a1fa3] text-white"
          }`}
        />
      </div>
    </>
  );
};

export default SelectionView;
